import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RefreshCw, Eye, Send, Mail, CheckCircle, XCircle, Clock, AlertTriangle, Inbox } from 'lucide-react';
import { toast } from 'sonner';

const EVENT_TYPES = ['delivered', 'hard_bounce', 'soft_bounce', 'complaint', 'blocked', 'deferred', 'opened', 'click', 'request'];
const STATUSES = ['success', 'fail', 'pending'];
const DELIVERY_STATUSES = ['pending', 'delivered', 'bounced', 'complained', 'unknown'];

const statusIcon = (s) => {
  if (s === 'success' || s === 'delivered') return <CheckCircle className="w-4 h-4 text-green-500" />;
  if (s === 'fail' || s === 'bounced' || s === 'complained') return <XCircle className="w-4 h-4 text-red-500" />;
  if (s === 'unknown') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <Clock className="w-4 h-4 text-yellow-500" />;
};

const deliveryBadge = (s) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-700',
    delivered: 'bg-green-100 text-green-700',
    bounced: 'bg-red-100 text-red-700',
    complained: 'bg-red-100 text-red-700',
    unknown: 'bg-amber-100 text-amber-700',
  };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded ${colors[s] || 'bg-gray-100 text-gray-700'}`}>{s}</span>;
};

export default function MailOps() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const VALID_TABS = ['events', 'outbox'];
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab = VALID_TABS.includes(rawTab) ? rawTab : 'events';

  const setTab = (newTab) => {
    setSearchParams({ tab: newTab }, { replace: tab === newTab });
  };

  // ── Events state ──
  const [events, setEvents] = useState([]);
  const [evLoading, setEvLoading] = useState(true);
  const [eventType, setEventType] = useState('');
  const [evStatus, setEvStatus] = useState('');
  const [evLimit, setEvLimit] = useState('50');

  // ── Outbox state ──
  const [outbox, setOutbox] = useState([]);
  const [obLoading, setObLoading] = useState(true);
  const [obStatus, setObStatus] = useState('');
  const [obLimit, setObLimit] = useState('50');

  // ── Shared ──
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchEvents = useCallback(async () => {
    setEvLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventType) params.set('eventType', eventType);
      if (evStatus) params.set('status', evStatus);
      params.set('limit', evLimit);
      const res = await axios.get(`${API}/admin/mail/events?${params}`, { headers });
      setEvents(res.data.events || []);
    } catch {
      toast.error('Failed to load mail events');
    } finally {
      setEvLoading(false);
    }
  }, [eventType, evStatus, evLimit]);

  const fetchOutbox = useCallback(async () => {
    setObLoading(true);
    try {
      const params = new URLSearchParams();
      if (obStatus) params.set('deliveryStatus', obStatus);
      params.set('limit', obLimit);
      const res = await axios.get(`${API}/admin/mail/outbox?${params}`, { headers });
      setOutbox(res.data.entries || []);
    } catch {
      toast.error('Failed to load outbox');
    } finally {
      setObLoading(false);
    }
  }, [obStatus, obLimit]);

  useEffect(() => { if (tab === 'events') fetchEvents(); }, [tab, fetchEvents]);
  useEffect(() => { if (tab === 'outbox') fetchOutbox(); }, [tab, fetchOutbox]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await axios.get(`${API}/admin/mail/events/${id}`, { headers });
      setDetail(res.data);
    } catch {
      toast.error('Failed to load event detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const sendTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/mail/test`, {}, { headers });
      setTestResult(res.data);
      toast.success('Test email sent');
      if (tab === 'outbox') fetchOutbox(); else fetchEvents();
    } catch (err) {
      if (err.response?.status === 404) {
        setTestResult({ ok: false, error: { code: 'PRODUCTION', message: 'Disabled in production' } });
      } else {
        toast.error('Failed to send test email');
      }
    } finally {
      setTestLoading(false);
    }
  };

  const tabBtn = (key, label, icon) => (
    <button
      onClick={() => setTab(key)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
        tab === key ? 'bg-orange-100 text-orange-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
      }`}
      data-testid={`tab-${key}`}
    >
      {icon} {label}
    </button>
  );

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FF6600' }}>Mail Operations</h1>
          <p className="text-gray-500 mt-1">Monitor transactional email delivery</p>
        </div>

        {/* Test Email Card */}
        <div className="bg-white rounded-xl border p-5 flex items-center justify-between" data-testid="test-email-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Send className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="font-medium text-gray-800">Send Test Email</p>
              <p className="text-sm text-gray-500">Sends TEST_EMAIL to {user?.email || 'your account'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {testResult && (
              <span className={`text-xs px-2 py-1 rounded ${testResult.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid="test-result-badge">
                {testResult.ok ? `Sent (${testResult.provider})` : testResult.error?.code || 'Error'}
              </span>
            )}
            <Button size="sm" onClick={sendTest} disabled={testLoading} data-testid="send-test-btn">
              {testLoading ? 'Sending...' : 'Send Test'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {tabBtn('events', 'Events', <Mail className="w-4 h-4" />)}
          {tabBtn('outbox', 'Outbox', <Inbox className="w-4 h-4" />)}
        </div>

        {/* ── Events Tab ── */}
        {tab === 'events' && (
          <>
            <div className="bg-white rounded-xl border p-5">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Event Type</label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger className="w-44" data-testid="filter-event-type"><SelectValue placeholder="All Events" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Events</SelectItem>
                      {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Status</label>
                  <Select value={evStatus} onValueChange={setEvStatus}>
                    <SelectTrigger className="w-36" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Limit</label>
                  <Select value={evLimit} onValueChange={setEvLimit}>
                    <SelectTrigger className="w-24" data-testid="filter-limit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={fetchEvents} disabled={evLoading} data-testid="refresh-btn">
                  <RefreshCw className={`w-4 h-4 mr-1 ${evLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="mail-events-table">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Template</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">To</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Provider</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evLoading ? (
                      <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
                    ) : events.length === 0 ? (
                      <tr><td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                        <Mail className="w-8 h-8 mx-auto mb-2 text-gray-300" />No events found
                      </td></tr>
                    ) : events.map((ev) => (
                      <tr key={ev.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`event-row-${ev.id}`}>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{new Date(ev.createdAt).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            ev.eventType === 'delivered' ? 'bg-green-100 text-green-700' :
                            ev.eventType === 'opened' || ev.eventType === 'click' ? 'bg-blue-100 text-blue-700' :
                            ev.eventType.includes('bounce') ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                          }`}>{ev.eventType}</span>
                        </td>
                        <td className="px-4 py-3"><div className="flex items-center gap-1.5">{statusIcon(ev.status)}<span className="text-sm text-gray-600">{ev.status}</span></div></td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ev.templateKey || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">{ev.toEmail || ev.toEmailMasked || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{ev.provider}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => openDetail(ev.id)} className="text-orange-600 hover:text-orange-700" data-testid={`view-event-${ev.id}`} title="View Details">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {events.length > 0 && <div className="px-4 py-3 border-t text-sm text-gray-500 bg-gray-50">Showing {events.length} events</div>}
            </div>
          </>
        )}

        {/* ── Outbox Tab ── */}
        {tab === 'outbox' && (
          <>
            <div className="bg-white rounded-xl border p-5">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Delivery Status</label>
                  <Select value={obStatus} onValueChange={setObStatus}>
                    <SelectTrigger className="w-44" data-testid="filter-delivery-status"><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {DELIVERY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500">Limit</label>
                  <Select value={obLimit} onValueChange={setObLimit}>
                    <SelectTrigger className="w-24" data-testid="filter-ob-limit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" onClick={fetchOutbox} disabled={obLoading} data-testid="refresh-outbox-btn">
                  <RefreshCw className={`w-4 h-4 mr-1 ${obLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </div>

            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="outbox-table">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Template</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">To</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Delivery</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Provider</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Correlation ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obLoading ? (
                      <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
                    ) : outbox.length === 0 ? (
                      <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                        <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />No outbox entries
                      </td></tr>
                    ) : outbox.map((ob) => (
                      <tr key={ob.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`outbox-row-${ob.id}`}>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{new Date(ob.createdAt).toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ob.templateKey || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono">{ob.toEmail || ob.toEmailMasked || '-'}</td>
                        <td className="px-4 py-3">{deliveryBadge(ob.deliveryStatus)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{ob.provider}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono">{ob.correlationId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {outbox.length > 0 && <div className="px-4 py-3 border-t text-sm text-gray-500 bg-gray-50">Showing {outbox.length} entries</div>}
            </div>
          </>
        )}

        {/* Detail Modal */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg" data-testid="event-detail-dialog">
            <DialogHeader><DialogTitle>Event Detail</DialogTitle></DialogHeader>
            {detailLoading ? (
              <div className="py-8 text-center text-gray-400">Loading...</div>
            ) : detail ? (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-gray-500">Type</span><p className="font-medium">{detail.eventType}</p></div>
                  <div><span className="text-gray-500">Status</span><div className="flex items-center gap-1">{statusIcon(detail.status)} <span className="font-medium">{detail.status}</span></div></div>
                  <div><span className="text-gray-500">Provider</span><p className="font-medium">{detail.provider}</p></div>
                  <div><span className="text-gray-500">Template</span><p className="font-medium">{detail.templateKey || '-'}</p></div>
                  <div><span className="text-gray-500">To</span><p className="font-mono">{detail.toEmail || detail.toEmailMasked || '-'}</p></div>
                  <div><span className="text-gray-500">Date</span><p>{new Date(detail.createdAt).toLocaleString()}</p></div>
                  {detail.correlationId && <div><span className="text-gray-500">Correlation ID</span><p className="font-mono text-xs break-all">{detail.correlationId}</p></div>}
                  {detail.errorCode && <div><span className="text-gray-500">Error</span><p className="text-red-600">{detail.errorCode}</p></div>}
                </div>
                <div className="font-mono text-xs break-all">
                  <span className="text-gray-500 block mb-1">Message ID</span>
                  {detail.messageId || '-'}
                </div>
                {detail.payload && (
                  <details className="border rounded-lg">
                    <summary className="px-3 py-2 cursor-pointer text-gray-500 hover:text-gray-700">Payload (sanitized)</summary>
                    <pre className="px-3 py-2 bg-gray-50 text-xs overflow-auto max-h-48 rounded-b-lg">{JSON.stringify(detail.payload, null, 2)}</pre>
                  </details>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
